import React from 'react'
import PropTypes from 'prop-types'

import Button from 'components/atoms/Button'
import AuthenticatedLink from 'components/molecules/AuthenticatedLink'
import Love from 'components/icons/Love'

import axios from 'axios'
import { branch } from 'baobab-react/higher-order'
import storage from 'utils/storage'
import API from 'utils/API'
import getNewIcon from 'utils/getNewIcon'

import Loader from 'components/utils/Loader'
import csrfToken from 'utils/csrfToken'
import hotjarTrigger from 'utils/hotjarTrigger'
import classNames from 'classnames'
import ActionTypeProp from 'utils/prop_types/ActionTypeProp'
import triggerEvent from 'utils/dataLayer'

class LikeButton extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      active: props.active,
      isLoading: false,
    }
    this.onClick = this.onClick.bind(this)
  }

  onClick() {
    if (!this.props.currentUser) {
      window.location.href = `/signin?isLead=true&successUrl=${encodeURIComponent(window.location.pathname)}`
      return null
    }
    if (this.state.isLoading) return null

    const {
      kind,
      targetId,
      forDetailPage,
      type,
      handleClick,
    } = this.props

    if (handleClick) {
      handleClick()
    }
    if (!this.props.currentUser.agreed_to_campus_terms) {
      storage.set('vpMetaNotice', true)
      return null
    }
    if (!this.props.currentUser.vp_meta) {
      window.location.href = `/signup/application?successUrl=${encodeURIComponent(window.location.pathname)}`
      return null
    }

    this.setState({ isLoading: true })

    return axios({
      url: API.endPoints().common.like,
      method: 'post',
      responseType: 'json',
      data: {
        id: targetId,
        kind,
      },
      headers: {
        'X-CSRF-Token': csrfToken(),
      },
    }).then((response) => {
      if (type) {
        triggerEvent(`ga/${type}/button-favorite`)
      }
      if (forDetailPage) {
        const targetData = storage.select('serverProps', kind.slice(0, -1))
        targetData.set('liked', response.data.liked)
        this.setState({
          isLoading: false,
          active: response.data.liked,
        }, () => {
          this.showHotjar()
        })
      } else {
        this.setState({
          isLoading: false,
          active: !this.state.active,
        }, () => {
          this.showHotjar()
        })
      }
    })
  }

  static getDerivedStateFromProps(props, state) {
    if (props.forDetailPage && (props.active !== state.active)) {
      return ({
        active: props.active,
      })
    }
    return null
  }

  showHotjar() {
    if (this.state.active) {
      const { kind } = this.props

      switch (kind) {
        case 'schools':
          hotjarTrigger('favourite_campus')
          break
        case 'studyFields':
          hotjarTrigger('favourite_major')
          break
        case 'professions':
          hotjarTrigger('favourite_career')
          break
        case 'scholarships':
          hotjarTrigger('favourite_scholarship')
          break
        default:
          // eslint-disable-next-line no-console
          console.log(`hotjar for ${kind} is not avaiable`)
      }
    }
  }

  render() {
    const {
      variant,
      className,
      children,
      currentUser,
      complete,
      forNavbar,
      useNewIcon,
      dataTestId,
    } = this.props

    const {
      isLoading,
      active,
    } = this.state

    const classNameRender = classNames(
      'like-button',
      className,
      {
        'like-button--active': active,
        'like-button--complete': complete,
        'like-button--for-navbar': forNavbar,
      },
    )
    const icon = () => {
      if (useNewIcon) {
        if (active) {
          return getNewIcon('heartActive', '#fb627a')
        }
        return getNewIcon('heartOutline', '#7d92a9')
      }
      return <Love className="pull-left m-x-xs" />
    }
    return (
      <Button
        dataTestId={dataTestId}
        variant={variant}
        className={classNameRender}
        onClick={this.onClick}
        component={currentUser ? 'div' : AuthenticatedLink}
      >
        <div className="like-button__content">
          {isLoading && <Loader className="like-button__loader" kind="doubleCircle" />}
          {
            !isLoading &&
            <>
              {icon()}
              {children}
            </>
          }
        </div>

        <style jsx global>
          {`
            .like-button {
              z-index: 1;
              padding: 4px;
              border-radius: 4px;

              &.like-button--complete {
                padding: 8px 8px;
                border: solid 1px #A3B4CA;
                svg {
                  #body {
                    fill: transparent;
                    stroke: #7d92a9;
                  }
                }
                span {
                  color: #485c77;
                  font-weight: bold;
                }
              }

              &--for-navbar {
                padding: 0;
              }

              @media screen and (min-width: 768px){
                .like-button__content {
                  svg {
                    margin-left: auto;
                    margin-right: auto;
                  }
                }
              }

              .like-button__content {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-evenly;
                svg {
                  animation-duration: 0.75s;
                  animation-name: bounceScale;
                }
                span {
                  color: #485c77;
                  margin-left: 8px;
                }
              }

              &.like-button--active {
                .like-button__content {
                  svg {
                    animation-name: rubberBand;
                    animation-duration: 1s;
                    animation-fill-mode: both;

                    #body {
                      fill: #fb627a;
                      stroke: #fff;
                    }
                  }
                }
              }

              .like-button__loader {
                width: 20px;
                display: block;
                padding-top : 4px;
                margin: 0 4px;
                filter: drop-shadow(-2px -1px 4px #fff);
              }
            }
          `}
        </style>
      </Button>
    )
  }
}

LikeButton.propTypes = {
  active: PropTypes.bool,
  kind: PropTypes.oneOf(['schools', 'studyFields', 'professions', 'scholarships']),
  targetId: PropTypes.string.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'secondary', 'orange', 'solid', 'link']),
  currentUser: PropTypes.object,
  complete: PropTypes.bool,
  forDetailPage: PropTypes.bool,
  forNavbar: PropTypes.bool,
  useNewIcon: PropTypes.bool,
  dataTestId: PropTypes.string,
  type: ActionTypeProp,
  handleClick: PropTypes.func,
}

LikeButton.defaultProps = {
  active: false,
  variant: 'link',
  forNavbar: false,
  useNewIcon: false,
}

export { LikeButton }
export default branch({
  currentUser: ['serverProps', 'currentUser'],
}, LikeButton)
